/**
 * RiverSoft Hard Water Impact Survey — riversoft-survey.js
 * Handles:
 *  - Step navigation with branching logic
 *  - Step 2 branch: Q5 (duration) → 2A (new city) | 2B (long term)
 *  - Step 3 branch: Q11 (filter usage) → 3A (no filter) | 3B (RiverSoft) | 3C (other brand)
 *  - Form data compilation into contact[body] for Shopify contact form
 *  - Basic required-field validation
 */

(function () {
  'use strict';

  // ─── State ───────────────────────────────────────────────────────────────────
  const state = {
    currentStep: 1,
    step2Branch: null,   // 'a' | 'b'
    step3Branch: null,   // 'a' | 'b' | 'c'
  };

  // ─── DOM refs ─────────────────────────────────────────────────────────────────
  const form        = document.getElementById('rs-survey-form');
  const progressFill  = document.getElementById('rs-progress-fill');
  const progressLabel = document.getElementById('rs-progress-label');
  const successDiv    = document.getElementById('rs-success');

  // ─── Progress ─────────────────────────────────────────────────────────────────
  function updateProgress(step) {
    const pct = { 1: 33, 2: 66, 3: 100 };
    const label = { 1: '1 of 3 Sections', 2: '2 of 3 Sections', 3: '3 of 3 Sections' };
    if (progressFill)  progressFill.style.width  = (pct[step] || 33) + '%';
    if (progressLabel) progressLabel.textContent = label[step] || '1 of 3 Sections';
  }

  // ─── Show / hide step ────────────────────────────────────────────────────────
  function showStep(stepId) {
    document.querySelectorAll('.rs-step, .rs-success').forEach(el => {
      el.classList.add('rs-step-hidden');
    });
    const el = document.getElementById(stepId);
    if (el) el.classList.remove('rs-step-hidden');
    window.scrollTo({ top: document.getElementById('rs-survey').offsetTop - 20, behavior: 'smooth' });
  }

  // ─── Determine Step 2 branch from Q5 ─────────────────────────────────────────
  function getStep2Branch() {
    const q5 = document.querySelector('input[name="q6_duration"]:checked');
    if (!q5) return null;
    return q5.value === 'Less than 1 year' ? 'a' : 'b';
  }

  // ─── Determine Step 3 branch from Q11 ────────────────────────────────────────
  function getStep3Branch() {
    const q11 = document.querySelector('input[name="q11_filter"]:checked');
    if (!q11) return null;
    if (q11.value === 'No') return 'a';
    if (q11.value === 'Yes, used RiverSoft') return 'b';
    return 'c'; // other brands
  }

  // ─── Validation ───────────────────────────────────────────────────────────────
  /**
   * Validates questions inside a given step element.
   * Returns true if valid.
   * Required: radio groups must have a selection, text inputs must not be empty.
   * Checkboxes are optional (multi-select) — not enforced unless specifically needed.
   */
  function validateStep(stepEl) {
    let valid = true;

    // Clear previous errors
    stepEl.querySelectorAll('.rs-has-error').forEach(el => el.classList.remove('rs-has-error'));
    stepEl.querySelectorAll('.rs-field-error').forEach(el => {
      el.classList.remove('rs-show');
      el.remove();
    });

    // Validate radio groups
    const radioGroups = {};
    stepEl.querySelectorAll('input[type="radio"]').forEach(r => {
      if (!radioGroups[r.name]) radioGroups[r.name] = [];
      radioGroups[r.name].push(r);
    });

    Object.entries(radioGroups).forEach(([name, radios]) => {
      const checked = radios.some(r => r.checked);
      if (!checked) {
        valid = false;
        const questionEl = radios[0].closest('.rs-question');
        if (questionEl) {
          questionEl.classList.add('rs-has-error');
          const err = document.createElement('p');
          err.className = 'rs-field-error rs-show';
          err.textContent = 'Please select an option.';
          questionEl.appendChild(err);
        }
      }
    });

    // Validate required text inputs (not phone fields, not hidden "other" inputs)
    stepEl.querySelectorAll('.rs-text-input:not([type="tel"]):not(.rs-inline-other)').forEach(inp => {
      // Skip inputs inside hidden wrappers
      const wrapper = inp.closest('.rs-other-input-wrap');
      if (wrapper && wrapper.style.display === 'none') return;
      // Skip inputs that are not visible
      if (inp.offsetParent === null) return;

      if (inp.value.trim() === '') {
        valid = false;
        const questionEl = inp.closest('.rs-question');
        if (questionEl) {
          questionEl.classList.add('rs-has-error');
          const err = document.createElement('p');
          err.className = 'rs-field-error rs-show';
          err.textContent = 'This field is required.';
          questionEl.appendChild(err);
        }
      }
    });

    // Validate "other" text inputs only when their parent wrapper is visible
    stepEl.querySelectorAll('.rs-other-input-wrap').forEach(wrap => {
      if (wrap.style.display === 'none') return;
      const inp = wrap.querySelector('.rs-inline-other');
      if (inp && inp.value.trim() === '') {
        valid = false;
        const questionEl = wrap.closest('.rs-question');
        if (questionEl && !questionEl.querySelector('.rs-field-error')) {
          questionEl.classList.add('rs-has-error');
          const err = document.createElement('p');
          err.className = 'rs-field-error rs-show';
          err.textContent = 'Please specify.';
          questionEl.appendChild(err);
        }
      }
    });

    return valid;
  }

  // ─── Human-readable question labels ──────────────────────────────────────────
  const QUESTION_LABELS = {
    // Step 1
    q1_city:          'Q1. Which city are you currently living in?',
    q1_city_other:    'Q1. City (specified)',
    q2_dwelling:      'Q2. What kind of dwelling do you live in?',
    q3_ownership:     'Q3. Is it owned or rented?',
    'q4_household[]': 'Q4. How many people live in your household?',
    q4_household:     'Q4. How many people live in your household?',
    q5_pincode:       'Q5. Residential pin code',
    q6_duration:      'Q6. How long have you been living here?',
    q7_water_source:  'Q7. What is the source of water in your house?',

    // Step 2A — New City Experience
    q2a_7_prev_city:       'Q8. What city/area did you previously live in?',
    q2a_8_water_quality:   'Q9. Did water quality feel different after moving?',
    q2a_9_awareness:       'Q10. Were you aware your area may have hard water?',
    'q2a_10_signs[]':      'Q11. First signs that made you suspect hard water',
    q2a_10_signs:          'Q11. First signs that made you suspect hard water',

    // Step 2B — Long Term Dwellers
    'q2b_7_problems[]':    'Q8. Hard water problems you are experiencing',
    q2b_7_problems:        'Q8. Hard water problems you are experiencing',
    q2b_8_awareness:       'Q9. Were you aware your area may have hard water?',
    q2b_9_greatest_impact: 'Q10. Which ONE problem has the greatest impact?',
    q2b_10_severity:       'Q11. Severity of hard water impact (1–5)',

    // Q11 shared
    q11_filter: 'Q12. Have you used any Hard Water Filters?',

    // Step 3A — No Filter
    'q3a_12_solutions[]':         'Q13. What kind of hard water solutions are you looking for?',
    q3a_12_solutions:             'Q13. What kind of hard water solutions are you looking for?',
    'q3a_13_riversoft_interest[]':'Q14. RiverSoft products you\'d like to know more about',
    q3a_13_riversoft_interest:    'Q14. RiverSoft products you\'d like to know more about',
    q3a_14_budget:                'Q15. Approximate budget for a hard water solution',
    q3a_15_phone:                 'Q16. Mobile number (optional)',

    // Step 3B — RiverSoft Brand Experience
    'q3b_12_rs_used[]':       'Q13. Which RiverSoft products have you used?',
    q3b_12_rs_used:           'Q13. Which RiverSoft products have you used?',
    q3b_13_rs_rating:         'Q14. How would you rate your RiverSoft experience?',
    'q3b_14_rs_interest[]':   'Q15. Other RiverSoft products you\'d like to try',
    q3b_14_rs_interest:       'Q15. Other RiverSoft products you\'d like to try',
    q3b_15_nps:               'Q16. Likelihood to recommend RiverSoft (1–10)',
    q3b_16_budget:            'Q17. Approximate budget for a hard water solution',
    q3b_17_phone:             'Q18. Mobile number (optional)',

    // Step 3C — Other Brand Experience
    q3c_12_brands:               'Q13. Which brands have you tried?',
    q3c_12_brands_other:         'Q13. Brand (specified)',
    'q3c_13_products[]':         'Q14. Which products have you used?',
    q3c_13_products:             'Q14. Which products have you used?',
    q3c_14_helpfulness:          'Q15. Was the product of any help?',
    'q3c_15_riversoft_interest[]':'Q16. RiverSoft products you\'d like to know more about',
    q3c_15_riversoft_interest:   'Q16. RiverSoft products you\'d like to know more about',
    q3c_16_budget:               'Q17. Approximate budget for a hard water solution',
    q3c_17_phone:                'Q18. Mobile number (optional)',
  };

  // ─── Step 2 branch label ──────────────────────────────────────────────────────
  function getStep2Label() {
    return state.step2Branch === 'a'
      ? 'STEP 2 — Experience After Moving to a New City'
      : 'STEP 2 — Long Term Dwellers: Hard Water Experience';
  }

  // ─── Step 3 branch label ──────────────────────────────────────────────────────
  function getStep3Label() {
    const map = {
      a: 'STEP 3 — Hard Water Product Requirement',
      b: 'STEP 3 — RiverSoft Brand Experience',
      c: 'STEP 3 — Other Brand Experience',
    };
    return map[state.step3Branch] || 'STEP 3';
  }

  // ─── Collect answers from a step element in order ─────────────────────────────
  function collectStepAnswers(stepEl) {
    const lines = [];
    const checkboxGroups = {}; // name → [values]
    const checkboxOrder = [];  // preserve insertion order

    // Walk all inputs in DOM order
    stepEl.querySelectorAll('input, textarea, select').forEach(inp => {
      if (inp.type === 'hidden') return;

      if (inp.type === 'checkbox') {
        const base = inp.name.replace('[]', '');
        if (!checkboxGroups[base]) {
          checkboxGroups[base] = [];
          checkboxOrder.push(base);
        }
        if (inp.checked) checkboxGroups[base].push(inp.value);
        return;
      }

      if (inp.type === 'radio') {
        if (!inp.checked) return;
        const label = QUESTION_LABELS[inp.name] || inp.name;
        lines.push({ name: inp.name, label, value: inp.value });
        return;
      }

      // text / tel / number
      if (inp.value.trim() === '') return;
      const label = QUESTION_LABELS[inp.name] || inp.name;
      lines.push({ name: inp.name, label, value: inp.value.trim() });
    });

    // Merge checkbox groups in the order they appeared in the DOM
    const result = [...lines];
    checkboxOrder.forEach(base => {
      const values = checkboxGroups[base];
      const label = QUESTION_LABELS[base + '[]'] || QUESTION_LABELS[base] || base;
      result.push({ name: base, label, value: values.length ? values.join(', ') : '(none selected)' });
    });

    // Sort by DOM order (lines already in order, checkboxes appended — re-sort by label Q number)
    // Actually lines are already in DOM order; checkboxes need to be interleaved correctly.
    // Simplest: rebuild fully in DOM order using question elements
    return buildOrderedLines(stepEl, checkboxGroups);
  }

  function buildOrderedLines(stepEl, checkboxGroups) {
    const lines = [];
    const processedCheckboxes = new Set();

    stepEl.querySelectorAll('.rs-question').forEach(qEl => {
      // Find the first input in this question to get its name
      const firstInput = qEl.querySelector('input:not([type="hidden"])');
      if (!firstInput) return;

      const name = firstInput.name;
      const baseName = name.replace('[]', '');

      if (firstInput.type === 'checkbox') {
        if (processedCheckboxes.has(baseName)) return;
        processedCheckboxes.add(baseName);
        const values = checkboxGroups[baseName] || [];
        const label = QUESTION_LABELS[name] || QUESTION_LABELS[baseName] || baseName;
        lines.push(`${label}: ${values.length ? values.join(', ') : '(none selected)'}`);
        return;
      }

      if (firstInput.type === 'radio') {
        const checked = qEl.querySelector(`input[type="radio"]:checked`);
        if (!checked) return;
        const label = QUESTION_LABELS[checked.name] || checked.name;
        lines.push(`${label}: ${checked.value}`);
        return;
      }

      // text / tel
      const inp = qEl.querySelector('input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]), textarea');
      if (inp && inp.value.trim()) {
        const label = QUESTION_LABELS[inp.name] || inp.name;
        lines.push(`${label}: ${inp.value.trim()}`);
      }
    });

    return lines;
  }

  // ─── Collect all answers into a readable string ───────────────────────────────
  function collectFormData() {
    const sections = [];

    // ── STEP 1 ──
    const step1El = document.getElementById('rs-step-1');
    if (step1El) {
      const checkboxGroups1 = buildCheckboxGroups(step1El);
      sections.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      sections.push('STEP 1 — Household Information');
      sections.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      buildOrderedLines(step1El, checkboxGroups1).forEach(l => sections.push(l));
    }

    // ── STEP 2 ──
    const step2Id = state.step2Branch === 'a' ? 'rs-step-2a' : 'rs-step-2b';
    const step2El = document.getElementById(step2Id);
    if (step2El) {
      const checkboxGroups2 = buildCheckboxGroups(step2El);
      sections.push('');
      sections.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      sections.push(getStep2Label());
      sections.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      buildOrderedLines(step2El, checkboxGroups2).forEach(l => sections.push(l));
    }

    // ── STEP 3 ──
    const step3Map = { a: 'rs-step-3a', b: 'rs-step-3b', c: 'rs-step-3c' };
    const step3El = document.getElementById(step3Map[state.step3Branch]);
    if (step3El) {
      const checkboxGroups3 = buildCheckboxGroups(step3El);
      sections.push('');
      sections.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      sections.push(getStep3Label());
      sections.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      buildOrderedLines(step3El, checkboxGroups3).forEach(l => sections.push(l));
    }

    return sections.join('\n');
  }

  function buildCheckboxGroups(stepEl) {
    const groups = {};
    stepEl.querySelectorAll('input[type="checkbox"]').forEach(inp => {
      const base = inp.name.replace('[]', '');
      if (!groups[base]) groups[base] = [];
      if (inp.checked) groups[base].push(inp.value);
    });
    return groups;
  }

  // ─── Sync phone to contact[phone] for Shopify ─────────────────────────────────
  function syncPhone() {
    const phoneInputs = [
      document.getElementById('q3a_15_phone'),
      document.getElementById('q3b_17_phone'),
      document.getElementById('q3c_17_phone'),
    ];
    const phoneField = document.getElementById('rs-form-phone');
    phoneInputs.forEach(inp => {
      if (inp && inp.value.trim() !== '') {
        if (phoneField) phoneField.value = inp.value.trim();
        // Also update email to use phone if no email provided
        const emailField = document.getElementById('rs-form-email');
        if (emailField && emailField.value === 'survey@riversoft.in') {
          emailField.value = inp.value.trim() + '@survey.riversoft.in';
        }
      }
    });
  }

  // ─── "Others" text field toggle ───────────────────────────────────────────────
  function initOtherToggles() {
    // Q1 city "Others"
    const q1Radios = document.querySelectorAll('input[name="q1_city"]');
    const q1OtherWrap = document.getElementById('q1-other-wrap');
    q1Radios.forEach(r => {
      r.addEventListener('change', () => {
        if (q1OtherWrap) q1OtherWrap.style.display = r.value === 'Others' && r.checked ? 'block' : 'none';
      });
    });

    // Q3C brand "Others"
    const q3cBrandOtherRadio = document.getElementById('q3c-brand-other-radio');
    const q3cBrandOtherWrap  = document.getElementById('q3c-brand-other-wrap');
    if (q3cBrandOtherRadio && q3cBrandOtherWrap) {
      document.querySelectorAll('input[name="q3c_12_brands"]').forEach(r => {
        r.addEventListener('change', () => {
          q3cBrandOtherWrap.style.display = q3cBrandOtherRadio.checked ? 'block' : 'none';
        });
      });
    }
  }

  // ─── Navigation ───────────────────────────────────────────────────────────────
  function handleNext(nextStep) {
    const currentStepId = getCurrentStepId();
    const currentStepEl = document.getElementById(currentStepId);

    if (currentStepEl && !validateStep(currentStepEl)) {
      return; // Stop if validation fails
    }

    if (nextStep === 2) {
      state.step2Branch = getStep2Branch();
      state.currentStep = 2;
      updateProgress(2);
      showStep(state.step2Branch === 'a' ? 'rs-step-2a' : 'rs-step-2b');

    } else if (nextStep === 3) {
      state.step3Branch = getStep3Branch();
      if (!state.step3Branch) {
        // Q11 not answered — show error
        const stepEl = document.getElementById(getCurrentStepId());
        const q11El = stepEl.querySelector('[data-name="q11_filter"]') ||
                      stepEl.querySelector('#q2a-q11, #q2b-q11');
        if (q11El) {
          const questionEl = q11El.closest('.rs-question');
          if (questionEl && !questionEl.querySelector('.rs-field-error')) {
            questionEl.classList.add('rs-has-error');
            const err = document.createElement('p');
            err.className = 'rs-field-error rs-show';
            err.textContent = 'Please select an option.';
            questionEl.appendChild(err);
          }
        }
        return;
      }
      state.currentStep = 3;
      updateProgress(3);
      const map = { a: 'rs-step-3a', b: 'rs-step-3b', c: 'rs-step-3c' };
      showStep(map[state.step3Branch]);
    }
  }

  function handleBack(backStep) {
    if (backStep === 1) {
      state.currentStep = 1;
      updateProgress(1);
      showStep('rs-step-1');
    } else if (backStep === 2) {
      state.currentStep = 2;
      updateProgress(2);
      showStep(state.step2Branch === 'a' ? 'rs-step-2a' : 'rs-step-2b');
    }
  }

  function getCurrentStepId() {
    if (state.currentStep === 1) return 'rs-step-1';
    if (state.currentStep === 2) return state.step2Branch === 'a' ? 'rs-step-2a' : 'rs-step-2b';
    if (state.currentStep === 3) {
      const map = { a: 'rs-step-3a', b: 'rs-step-3b', c: 'rs-step-3c' };
      return map[state.step3Branch] || 'rs-step-3a';
    }
    return 'rs-step-1';
  }

  // ─── Form Submit ──────────────────────────────────────────────────────────────
  function handleSubmit(e) {
    e.preventDefault();

    const currentStepEl = document.getElementById(getCurrentStepId());
    if (currentStepEl && !validateStep(currentStepEl)) return;

    // Sync phone
    syncPhone();

    // Build body
    const bodyField = document.getElementById('rs-form-body');
    if (bodyField) {
      bodyField.value = '=== RiverSoft Hard Water Impact Survey ===\n\n' + collectFormData();
    }

    // Submit via fetch to avoid page reload (Shopify contact form)
    const formData = new FormData(form);

    fetch('/contact', {
      method: 'POST',
      body: formData,
      headers: { 'Accept': 'application/json' },
    })
    .then(res => {
      // Shopify contact form always redirects on success (200/302)
      // Show success state regardless
      showSuccessState();
    })
    .catch(() => {
      showSuccessState();
    });
  }

  function showSuccessState() {
    document.querySelectorAll('.rs-step').forEach(el => el.classList.add('rs-step-hidden'));
    if (successDiv) successDiv.classList.remove('rs-step-hidden');
    if (progressFill) progressFill.style.width = '100%';
    if (progressLabel) progressLabel.textContent = 'Complete!';
    window.scrollTo({ top: document.getElementById('rs-survey').offsetTop - 20, behavior: 'smooth' });
    setTimeout(function () {
      window.location.href = '/';
    }, 3000);
  }

  // ─── Event delegation ─────────────────────────────────────────────────────────
  document.addEventListener('click', function (e) {
    const nextBtn = e.target.closest('.rs-btn-next');
    const backBtn = e.target.closest('.rs-btn-back');
    const submitBtn = e.target.closest('.rs-btn-submit');

    if (nextBtn) {
      const nextStep = parseInt(nextBtn.dataset.next, 10);
      handleNext(nextStep);
    }
    if (backBtn) {
      const backStep = parseInt(backBtn.dataset.back, 10);
      handleBack(backStep);
    }
    if (submitBtn && form) {
      handleSubmit({ preventDefault: () => {} });
    }
  });

  if (form) {
    form.addEventListener('submit', handleSubmit);
  }

  // ─── Init ─────────────────────────────────────────────────────────────────────
  function init() {
    updateProgress(1);
    showStep('rs-step-1');
    initOtherToggles();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();